import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useAuth } from "@/context/AuthContext";
import { useDirectory } from "@/lib/rights";

export const NONE = "__none";
const BLANK = { name: "", legal_name: "", service_type: "SaaS Application", owner: "", country: "India", website: "", description: "",
  hosting: NONE, access_level: "READ_WRITE", business_criticality: "MEDIUM", processing_position: NONE, record_volume_band: NONE };

export const Field = ({ label, children, hint, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

export const Pick = ({ value, onChange, options, testId, none, disabled }) => (
  <Select value={value ?? NONE} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-9 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {none && <SelectItem value={NONE}>{none}</SelectItem>}
      {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const unNone = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v === NONE ? null : v]));

export const NewVendorDialog = ({ open, onOpenChange, meta }) => {
  const auth = useAuth();
  const nav = useNavigate();
  const users = useDirectory();
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (open) { setF({ ...BLANK, owner: auth.user?.username || "" }); setTouched(false); } }, [open, auth.user?.username]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const nameError = touched && !f.name.trim() ? "Vendor name is required" : null;

  const submit = async () => {
    if (!f.name.trim()) { setTouched(true); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vendors", unNone(f));
      toast.success(`${data.vendor_id} added to the register`);
      onOpenChange(false);
      nav(`/vendors/${data.id}?tab=ropa`);
    } catch (e) { toast.error(apiError(e, "Could not add vendor")); } finally { setSaving(false); }
  };
  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-vendor-dialog">
        <DialogHeader>
          <DialogTitle>Add vendor</DialogTitle>
          <DialogDescription>The risk rating is calculated from these inputs plus the RoPA data classes you link next.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Vendor name">
            <Input
              value={f.name} onChange={set("name")} onBlur={() => setTouched(true)}
              aria-invalid={!!nameError} className={nameError ? "border-red-400 focus-visible:ring-red-400" : ""}
              data-testid="new-vendor-name"
            />
            {nameError && <p className="text-[11px] text-red-600" data-testid="new-vendor-name-error">{nameError}</p>}
          </Field>
          <Field label="Legal entity"><Input value={f.legal_name} onChange={set("legal_name")} data-testid="new-vendor-legal-name" /></Field>
          <Field label="Service type"><Pick value={f.service_type} onChange={set("service_type")} options={meta.service_types.map((s) => [s, s])} testId="new-vendor-service" /></Field>
          <Field label="Owner"><Pick value={f.owner} onChange={set("owner")} options={users.map((u) => [u.username, u.full_name || u.username])} testId="new-vendor-owner" /></Field>
          <Field label="Country"><Input value={f.country} onChange={set("country")} data-testid="new-vendor-country" /></Field>
          <Field label="Hosting" hint="Leave blank to derive it from linked RoPA assets">
            <Pick value={f.hosting} onChange={set("hosting")} none="Derive from RoPA" options={Object.entries(meta.hosting)} testId="new-vendor-hosting" />
          </Field>
          <Field label="Access level"><Pick value={f.access_level} onChange={set("access_level")} options={Object.entries(meta.access_levels)} testId="new-vendor-access" /></Field>
          <Field label="Business criticality"><Pick value={f.business_criticality} onChange={set("business_criticality")} options={Object.entries(meta.criticality)} testId="new-vendor-criticality" /></Field>
          <Field label="Processing position" hint="Blank uses the linked RoPA party type">
            <Pick value={f.processing_position} onChange={set("processing_position")} none="Derive from RoPA" options={Object.entries(meta.positions)} testId="new-vendor-position" />
          </Field>
          <Field label="Record volume" hint="Blank uses linked RoPA activities">
            <Pick value={f.record_volume_band} onChange={set("record_volume_band")} none="Derive from RoPA" options={meta.volume_bands.map((b) => [b, b])} testId="new-vendor-volume" />
          </Field>
          <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="new-vendor-description" /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !f.name.trim()} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="new-vendor-submit">
            {saving ? "Adding…" : "Add vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
